/**
 * Letter Promo Widget - Compact promotional card
 * Shows the free Personalised Letter offer with product image
 * Supports multiple themes: 'generic' (default), 'christmas'
 *
 * Features:
 * - Product image from Personalised Letter
 * - Clean, adaptable design
 * - FREE badge with UK pricing (£)
 * - Click to open detailed modal
 * - Theme-aware content (generic vs christmas)
 *
 * @version 2.0.0
 */

(function(window) {
  'use strict';

  // Theme configurations
  const THEMES = {
    generic: {
      title: 'FREE Personalised Letter',
      description: 'Personalised letter included with every ride-on',
      modalTitle: 'Your FREE Personalised Letter',
      modalHeading: 'Every Ride-On Includes a FREE Personalised Letter!',
      modalDescription: 'Add special memories with a beautifully personalised letter that makes your child feel truly special',
      features: [
        { title: 'Beautiful Templates', subtitle: 'Professionally designed' },
        { title: 'Fully Personalised', subtitle: "Child's name & details" },
        { title: 'Multiple Occasions', subtitle: 'Birthday, Tooth Fairy & more' }
      ],
      featureIcon: 'sparkle', // sparkle for generic
      gradient: 'linear-gradient(135deg, rgb(var(--color-drawer-text) / 0.1) 0%, rgb(var(--color-drawer-text) / 0.05) 100%)',
      showSnowflakes: false,
      badgeColor: 'rgb(var(--color-base-button))'
    },
    christmas: {
      title: 'FREE Santa Letter',
      description: 'Personalised letter from Santa included with every ride-on',
      modalTitle: 'Your FREE Santa Letter',
      modalHeading: 'Every Ride-On Includes a FREE Personalised Letter from Santa!',
      modalDescription: 'Add magical Christmas memories with a beautifully personalised letter that makes your child feel truly special',
      features: [
        { title: 'Beautiful Templates', subtitle: 'Festive designs' },
        { title: 'Fully Personalised', subtitle: "Child's name & details" },
        { title: 'Christmas Magic', subtitle: "From Santa's workshop" }
      ],
      featureIcon: 'magic', // star for christmas
      gradient: 'linear-gradient(135deg, #C41E3A 0%, #165B33 100%)',
      showSnowflakes: true,
      badgeColor: '#C41E3A'
    }
  };

  class SantaLetterPromoWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      // Load immediately (not lazy loaded)
      this.lazyLoad = false;

      // Widget state
      this.letterAddon = null;
      this.letterProduct = null;
      this.modalId = `santa-letter-promo-modal-${config.id}`;
      this.theme = null; // Set during init
    }

    /**
     * Get current theme configuration
     */
    getThemeConfig() {
      const themeName = this.bundleConfig?.settings?.letterPromoTheme || 'generic';
      return THEMES[themeName] || THEMES.generic;
    }

    /**
     * Initialize widget - fetch letter addon and product data
     */
    async init() {
      // Get letter addon from bundle config
      // Check if Santa letters are globally enabled
      const santaLettersEnabled = this.bundleConfig?.settings?.santaLettersEnabled !== false;

      if (!santaLettersEnabled) {
        this.letterAddon = null;
        return;
      }

      if (this.bundleConfig && this.bundleConfig.addons && this.bundleConfig.addons.personalization) {
        this.letterAddon = this.bundleConfig.addons.personalization.find(
          addon => addon.productHandle === 'personalised-letter' && addon.alwaysIncluded && addon.enabled !== false
        );
      }

      // Check if product is eligible
      if (this.letterAddon && !this.manager.isAddonEligible(this.letterAddon)) {
        this.letterAddon = null;
      }

      // Fetch actual product data for image
      if (this.letterAddon) {
        try {
          const response = await fetch('/products/personalised-letter.js');
          if (response.ok) {
            this.letterProduct = await response.json();
          }
        } catch (error) {
          console.warn('[SantaLetterPromoWidget] Could not fetch product data:', error);
        }
      }

      if (!this.letterAddon) {
        console.warn('[SantaLetterPromoWidget] No letter addon found or product not eligible');
      }
    }

    /**
     * Render card to container
     */
    render() {
      // Don't render if no letter addon or not eligible
      if (!this.letterAddon) {
        this.hide();
        return;
      }

      // Build card + modal inline (x-modal pattern)
      // Use setContent() to preserve skeleton loading
      this.setContent(`
        ${this.buildCardHTML()}
        ${this.buildModalHTML()}
      `);

      // Emit render event
      this.emit('santa-letter-promo:rendered', { hasLetterAddon: !!this.letterAddon });
    }

    /**
     * Build compact promotional card HTML
     */
    buildCardHTML() {
      const theme = this.getThemeConfig();
      const productImage = this.letterProduct?.featured_image || this.letterProduct?.images?.[0];
      const imageUrl = productImage || 'https://cdn.shopify.com/s/files/1/0568/4081/9752/files/letter-placeholder.jpg';

      // Build snowflakes HTML only for christmas theme
      const snowflakesHTML = theme.showSnowflakes ? `
        <div class="pw-slp-snowflakes" aria-hidden="true">
          <div class="pw-slp-snowflake" style="left: 20%; animation-delay: 0s;">❄</div>
          <div class="pw-slp-snowflake" style="left: 60%; animation-delay: 1.5s;">❄</div>
          <div class="pw-slp-snowflake" style="left: 85%; animation-delay: 0.7s;">❄</div>
        </div>
      ` : '';

      return `
        <div class="pw-santa-letter-promo-widget" style="width: 100%;">
          <div class="pw-widget--card" style="padding: 0; overflow: hidden; width: 100%;">

            <!-- Split Layout: Image Left, Content Right -->
            <div style="display: grid; grid-template-columns: 130px 1fr; gap: 0;">

              <!-- Image Section with Theme Overlay -->
              <div style="position: relative; background: ${theme.gradient}; overflow: hidden;">

                <!-- Product Image -->
                <img
                  src="${imageUrl}"
                  alt="Free Personalised Letter"
                  style="width: 100%; height: 100%; object-fit: cover; opacity: 0.9; ${theme.showSnowflakes ? 'mix-blend-mode: luminosity;' : ''}"
                />

                ${snowflakesHTML}

                <!-- FREE Badge (bottom of image) -->
                <div style="position: absolute; bottom: var(--sp-2); left: 50%; transform: translateX(-50%); background: white; color: ${theme.badgeColor}; padding: 4px 12px; border-radius: var(--rounded-full); font-weight: 700; font-size: 0.75rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2); z-index: 3; white-space: nowrap;">
                  FREE
                </div>
              </div>

              <!-- Content Section -->
              <div style="padding: var(--sp-3) var(--sp-4); display: flex; flex-direction: column; gap: var(--sp-2);">

                <!-- Title + Worth Badge -->
                <div>
                  <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: var(--sp-2); margin-bottom: var(--sp-2);">
                    <h4 style="margin: 0; font-size: 0.9375rem; font-weight: 700; line-height: 1.3; color: rgb(var(--color-drawer-text));">
                      ${theme.title}
                    </h4>
                    <div style="background: rgb(var(--color-drawer-text) / 0.08); color: rgb(var(--color-drawer-text) / 0.7); padding: 2px 8px; border-radius: var(--rounded-full); font-size: 0.6875rem; font-weight: 600; white-space: nowrap; flex-shrink: 0;">
                      Worth £10
                    </div>
                  </div>

                  <!-- Description -->
                  <p style="margin: 0; font-size: 0.8125rem; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.4;">
                    ${theme.description}
                  </p>

                  ${this.getShippingMessage()}
                </div>

                <!-- Subtle Text Link -->
                <button
                  type="button"
                  aria-controls="${this.modalId}"
                  aria-expanded="false"
                  style="background: none; border: none; padding: 0; color: rgb(var(--color-drawer-text) / 0.7); font-size: 0.8125rem; cursor: pointer; transition: color 0.2s; display: inline-flex; align-items: center; gap: 4px; text-decoration: underline; text-underline-offset: 2px;"
                  onmouseover="this.style.color='rgb(var(--color-drawer-text))';"
                  onmouseout="this.style.color='rgb(var(--color-drawer-text) / 0.7)';">
                  <svg style="width: 14px; height: 14px;" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  See details
                </button>

              </div>

            </div>

          </div>
        </div>
      `;
    }

    /**
     * Build modal HTML structure using x-modal (matches Size Chart pattern EXACTLY)
     */
    buildModalHTML() {
      const theme = this.getThemeConfig();
      const featureIcons = [
        this.getLetterIconSVG(),
        this.getPersonalizationIconSVG(),
        theme.featureIcon === 'magic' ? this.getMagicIconSVG() : this.getSparkleIconSVG()
      ];

      return `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="${theme.modalTitle}" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">${theme.modalTitle}</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section -->
                <div class="text-center">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: rgb(var(--color-drawer-text)); border-radius: 50%; margin-bottom: 1rem;">
                    <svg style="width: 2rem; height: 2rem; color: rgb(var(--color-drawer-background));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem;">
                    ${theme.modalHeading}
                  </h3>
                  <p class="text-base text-opacity" style="max-width: 500px; margin: 0 auto;">
                    ${theme.modalDescription}
                  </p>
                </div>

                <!-- Features Grid - Horizontal Scroll Wrapper -->
                <div style="margin: 0 -1.5rem;">
                  <div style="display: flex; gap: 1rem; overflow-x: auto; -webkit-overflow-scrolling: touch; padding: 0 1.5rem; scrollbar-width: none;">
                    ${theme.features.map((feature, i) => `
                      <div class="text-center" style="flex: 0 0 auto; width: 200px; padding: 1.25rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: 8px; border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                        <div style="display: flex; align-items: center; justify-content: center; width: 3rem; height: 3rem; background: rgb(var(--color-drawer-text) / 0.08); border-radius: 8px; margin: 0 auto 0.75rem;">
                          ${featureIcons[i]}
                        </div>
                        <p class="font-bold text-sm" style="margin-bottom: 0.25rem;">${feature.title}</p>
                        <p class="text-xs text-opacity">${feature.subtitle}</p>
                      </div>
                    `).join('')}
                  </div>
                </div>

                <!-- What's Included Section -->
                <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: 8px; border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                  <h4 class="font-bold text-base" style="margin-bottom: 1rem; display: flex; align-items: center; gap: 0.5rem;">
                    ${this.getGiftIconSVG()}
                    What's Included:
                  </h4>
                  <ul class="text-sm" style="list-style: none; padding: 0; margin: 0;">
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Choose from professionally designed letter templates</span>
                    </li>
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Personalise with your child's name, age, and achievements</span>
                    </li>
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Printed and delivered with your ride-on</span>
                    </li>
                    <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.5rem;">
                      <svg style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; margin-top: 0.125rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>Easy customisation in your cart - takes less than 2 minutes</span>
                    </li>
                  </ul>
                </div>

                <!-- CTA Section -->
                <div class="text-center">
                  <div style="padding: 1.25rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: 8px; border: 2px dashed rgb(var(--color-drawer-text) / 0.15);">
                    <p class="font-bold text-base" style="margin-bottom: 0.25rem;">
                      ${this.getGiftIconSVG()} Completely FREE with your purchase!
                    </p>
                    <p class="text-sm text-opacity" style="margin: 0;">
                      Configure your letter after adding to cart
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }

    /**
     * Cleanup
     */
    destroy() {
      // Modal is inside container, will be cleaned up automatically
      super.destroy();
    }

    // SVG Icons
    getShippingMessage() {
      const shippingMessage = this.bundleConfig?.settings?.santaLetterShippingMessage;
      if (!shippingMessage) return '';

      return `
        <div style="margin-top: var(--sp-2); padding: var(--sp-2) var(--sp-3); background: rgb(var(--color-drawer-text) / 0.05); border-left: 3px solid rgb(var(--color-base-button)); border-radius: 4px; display: flex; align-items: center; gap: var(--sp-2);">
          <svg style="width: 14px; height: 14px; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p style="margin: 0; font-size: 0.75rem; font-weight: 600; color: rgb(var(--color-drawer-text) / 0.8);">
            ${shippingMessage}
          </p>
        </div>
      `;
    }

    getGiftIconSVG() {
      return `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M20 12V22H4V12M2 7H22V12H2V7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 22V7M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C11 2 12 7 12 7ZM12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C13 2 12 7 12 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    getLetterIconSVG() {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    getPersonalizationIconSVG() {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    getMagicIconSVG() {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    getSparkleIconSVG() {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M12 3L13.5 8.5L19 10L13.5 11.5L12 17L10.5 11.5L5 10L10.5 8.5L12 3Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M19 3L19.5 5L21.5 5.5L19.5 6L19 8L18.5 6L16.5 5.5L18.5 5L19 3Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M5 14L5.5 16L7.5 16.5L5.5 17L5 19L4.5 17L2.5 16.5L4.5 16L5 14Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }
  }

  // Expose widget class
  if (!window.ProductWidgets || !window.ProductWidgets.widgets) {
    window.ProductWidgets = window.ProductWidgets || {};
    window.ProductWidgets.widgets = window.ProductWidgets.widgets || {};
  }

  window.ProductWidgets.widgets.SantaLetterPromoWidget = SantaLetterPromoWidget;

})(window);
